import { EventBus } from '@/plugins/event-bus'
import Quote from 'api-client/Quote.api'
import PrintOptionsApi from '@/api/server/PrintOptions'
import Vehicle from 'api-client/Vehicle'
import Customers from 'api-client/Customers'
import axios from 'axios'
import {
  getUnknownExchangeCar as mappers_getUnknownExchangeCar,
  mapVehicleInfoToExchangeCar as mappers_mapVehicleInfoToExchangeCar,
} from '@/mappers/ExchangeCarMapper'

import digitalSigningStatus from '@/helpers/enums/digitalSigningStatus'
import printOptions from './quote.printOptions'
import priceDisplayMode from '@/helpers/enums/priceDisplayMode'
import arrayFunctions from '../../helpers/arrayFunctions'
import ObjectHelper from '../../helpers/objectHelper'
import { stripEnrichment } from '@/helpers/ponCatalog'

// A quote save that never clears isSaving must not block the print options forever.
const PRINT_OPTIONS_MAX_WAIT_MS = 30000

const emitStoreEvents = () => {
  // called after every mutation.
  EventBus.$emit('quote/get')
  EventBus.$emit('quote/deliveryDate')
  EventBus.$emit('quote/doNotDeliverBeforeDate')
  EventBus.$emit('quote/deliverAsSoonAsPossible')
  EventBus.$emit('quote/expireDate')
  EventBus.$emit('quote/totalPrice')
  EventBus.$emit('quote/amountDue')
  EventBus.$emit('quote/accessories')
  EventBus.$emit('quote/customer')
  EventBus.$emit('quote/customerCompany')
  EventBus.$emit('quote/accessoriesWithCategories')
  EventBus.$emit('quote/controlCodes')
  EventBus.$emit('quote/isSaving')
  EventBus.$emit('quote/exchangeCars')
  EventBus.$emit('quote/hasExchangeCars')
  EventBus.$emit('quote/salesOrderSendToAutoline')
  EventBus.$emit('quote/quoteStatusIsSalesOrder')
  EventBus.$emit('quote/loading')
  EventBus.$emit('quote/isStockOrder')
  EventBus.$emit('quote/car')
  EventBus.$emit('quote/employee')
  EventBus.$emit('quote/dealer')
  EventBus.$emit('quote/isStockCar')
  EventBus.$emit('quote/isNew')
  EventBus.$emit('quote/hasCustomer')
  EventBus.$emit('quote/leaseCompany')
  EventBus.$emit('quote/hasLeaseCompany')
  EventBus.$emit('quote/fleetContract')
  EventBus.$emit('quote/hasCustomerCompany')
  EventBus.$emit('quote/hasOwner')
  EventBus.$emit('quote/hasPdfOptions')
  EventBus.$emit('quote/validationMessages')
  EventBus.$emit('quote/leaseReference')
  EventBus.$emit('quote/priceDisplayMode')
  EventBus.$emit('quote/isPriceShowIncl')
  EventBus.$emit('quote/isPriceShowExcl')
}

const isProductInSelectedProducts = (state, product) => {
  return state.quote.QuoteDetails.DealerAccessories.find((p) => isEqualProducts(product, p))
}

const getCompanyReferenceNumber = (leaseCompany) => {
  if (leaseCompany.CustomerCompanySource === 'AUTOLINE') {
    return leaseCompany.CustomerCompanyMagicNumber
  }
  if (leaseCompany.CustomerCompanySource === 'CMDM') {
    if (leaseCompany.CustomerCompanyChamberOfCommerceNumber) {
      return leaseCompany.CustomerCompanyChamberOfCommerceNumber
    } else if (leaseCompany.ChamberOfCommerceNumber) {
      return leaseCompany.ChamberOfCommerceNumber
    }
  }
}

// customerPoId is a varchar the whole way down (quote.customer_po_id, the
// Customer entity, every DTO). Values that look numeric use 0 and -1 as the
// "no CMDM customer" sentinel, so those still count as absent; a CMDM id such
// as CUST-00001 is not a number at all and does count.
const hasPoId = (poId) => {
  if (poId == null || poId === '') return false
  const asNumber = Number(poId)
  return Number.isNaN(asNumber) ? true : asNumber > 0
}

const getCustomerPoId = (customerCompany) => {
  if (customerCompany.CustomerCompanyMagicNumber) {
    return customerCompany.CustomerCompanyMagicNumber
  } else if (customerCompany.CustomerCompanyPoId) {
    return customerCompany.CustomerCompanyPoId
  }
}

const isEqualProducts = (product, selectedProduct) => {
  //Since selectedProducts might have duplicate OptionType_ID's we want to match using the index. If a product does not have an index it is
  //matched from the all products list. In that case it is only a match if the product was not manually added (i.e. IsPCQMGeneratedId = false).
  //The reason for this is that manually added products always get the same OptionType_ID (i.e. D-PON920).
  if (selectedProduct.IsPCQMGeneratedId) {
    if (product.hasOwnProperty('Index')) {
      return selectedProduct.Index === product.Index
    } else return false
  }

  return selectedProduct.OptionType_ID === product.OptionType_ID
}

const addIndex = (products) => {
  products.forEach((item, index) => (item.Index = index))
  return products
}

const getters = {
  get: (state) => state.quote,
  car: (state) => state.car,
  quoteId: (state) => state.quote?.QuoteId,
  childQuotes: (state) => state.quote.ChildQuotes,
  isStockCar: (state) => state.car?.Type === 'STOCKCAR' || state.car?.Type === 'STOCKCAR_V2',
  deliveryDate: (state) => state.quote?.QuoteDetails?.DeliveryDate,
  expireDate: (state) => state.quote?.QuoteDetails?.ExpireDate,
  accessories: (state) => state.quote?.QuoteDetails?.DealerAccessories,
  accessoriesWithCategories: (state) => (categories) => {
    return state.quote?.QuoteDetails.DealerAccessories.filter((p) =>
      categories.includes(p.Category)
    )
  },
  controlCodes: (state) => state.quote?.QuoteDetails?.ControlCodes,
  customerCompany: (state) => state.quote?.QuoteDetails?.CustomerCompany,
  leaseCompany: (state) => state.quote?.QuoteDetails?.LeaseCompany,
  fleetContract: (state) => state.quote?.QuoteDetails?.FleetSalesContract,
  customer: (state) => state.quote?.QuoteDetails?.Customer,
  customerZipcode: (state) => state.quote.QuoteDetails?.Customer?.CustomerZipCode,
  customerCountryCode: (state) => state.quote.QuoteDetails?.Customer?.CustomerCountryCode,
  status: (state) => state?.quote?.Status,
  signingStatus: (state) => state?.quote?.SigningStatus,
  linkedChangeOrders: (state) => state?.quote?.LinkedChangeOrders,
  quoteDetails: (state) => state?.quote?.QuoteDetails,
  quoteStatusIsQuote: (state) => state?.quote?.Status?.endsWith('QUOTE'),
  quoteStatusIsRejected: (state) => state?.quote?.Status?.endsWith('REJECTED'),
  quoteStatusIsConcept: (state) => state?.quote?.Status?.endsWith('CONCEPT'),
  quoteStatusIsSalesOrder: (state) => state?.quote?.Status?.endsWith('SALESORDER'),
  quoteSigningStatusIsEmpty: (state) =>
    !state?.quote?.SigningStatus ||
    state?.quote?.SigningStatus?.endsWith(digitalSigningStatus.failedToSend),
  quoteSigningStatusIsPending: (state) =>
    state?.quote?.SigningStatus?.endsWith(digitalSigningStatus.sentToCustomer) ||
    state?.quote?.SigningStatus?.endsWith(digitalSigningStatus.openedByCustomer) ||
    state?.quote?.SigningStatus?.endsWith(digitalSigningStatus.manualSignPending),
  quoteSigningStatusIsCompleted: (state) =>
    state?.quote?.SigningStatus?.endsWith(digitalSigningStatus.signCompleted) ||
    state?.quote?.SigningStatus?.endsWith(digitalSigningStatus.signedByCustomer) ||
    state?.quote?.SigningStatus?.endsWith(digitalSigningStatus.documentSignedByCustomer) ||
    state?.quote?.SigningStatus?.endsWith(digitalSigningStatus.manualSignCompleted),
  quoteStatusChangeDate: (state) => {
    const history = state?.quote?.Metadata?.History
    if (!history) {
      return null
    }
    const reverseHistory = [...history].reverse()
    return reverseHistory?.find((x) => x.NewStatus === state.quote.Status)?.UpdatedOn
  },
  salesOrderSendToAutoline: (state) => state?.quote?.SalesOrderSendToAutoline,
  quoteSendToAutoline: (state) => state?.quote?.QuoteSendToAutoline,
  loading: (state) => state?.loading,
  isSaving: (state) => state?.isSaving,
  totalPrice: (state) => state.quote?.Metadata?.Prices?.TotalPrice,
  dealerAccessoriesTotalPrice: (state) =>
    state.quote?.Metadata?.Prices?.DealerAccessoriesTotalPrice,
  amountDue: (state) => state.quote?.Metadata?.Prices?.AmountDue,
  customCorrectionProduct: (state) => (correctionAnalysisCode) => {
    if (correctionAnalysisCode == '') return null
    else
      return state.quote.QuoteDetails.DealerAccessories.find(
        (p) => p.AnalysisCode == correctionAnalysisCode
      )
  },
  isCreating: (state) => state?.isCreating,
  exchangeCars: (state) => state.quote?.QuoteDetails?.ExchangeCars || [],
  hasExchangeCars: (state) => {
    const cars = state.quote?.QuoteDetails?.ExchangeCars || []
    return cars.length > 0 && cars.some((car) => car.LicensePlate)
  },
  exchangeCarsCount: (state) => {
    const cars = state.quote?.QuoteDetails?.ExchangeCars || []
    return cars.filter((car) => car.LicensePlate).length
  },
  exchangeCarType: (state) => {
    const cars = state.quote?.QuoteDetails?.ExchangeCars || []
    if (cars.length > 0) {
      let carType = cars[0].Type
      return carType
    }

    return 'MARGE'
  },
  isStockOrder: (state) => state.quote.QuoteDetails?.IsStockOrder,
  isNew: (state) => !state.quote.QuoteId,
  customerType: (state) => state.quote?.QuoteDetails?.CustomerType,
  leaseReference: (state) => state.quote.QuoteDetails?.LeaseReference,
  priceDisplayMode: (state) => state.quote?.QuoteDetails?.PriceDisplayMode,
  isPriceShowIncl: (state) => getters.priceDisplayMode(state) != priceDisplayMode.excluding.value,
  isPriceShowExcl: (state) => getters.priceDisplayMode(state) != priceDisplayMode.including.value,
  showVwpfsFinancePdf: (state) => state.quote.QuoteDetails?.ShowVwpfsFinancePdf,
  showVwpfsPrivateLeasePdf: (state) => state.quote.QuoteDetails?.ShowVwpfsPrivateLeasePdf,
  showVwpfsInsurancePdf: (state) => state.quote.QuoteDetails?.ShowVwpfsInsurancePdf,
  showVwpfsOperationalLeasePdf: (state) => state.quote.QuoteDetails?.ShowVwpfsOperationalLeasePdf,
  stockButtonDate: (state) => state.quote.QuoteDetails?.StockButtonDate,
  plQuotationId: (state) => state.quote.QuoteDetails?.PlQuotationId,
  hasLeaseReference: (state) => state.quote.QuoteDetails?.LeaseReference != null,
  hasLeaseCompany: (state) => state.quote?.QuoteDetails?.LeaseCompany?.Name != null,
  hasCustomerCompany: (state) =>
    state.quote?.QuoteDetails?.CustomerCompany?.CustomerCompanyName != null &&
    state.quote?.QuoteDetails?.CustomerCompany?.CustomerCompanyName != '',
  hasCustomer: (state) =>
    hasPoId(state.quote?.QuoteDetails?.Customer?.PoId) ||
    state.quote?.QuoteDetails?.Customer?.CustomerMagicNumber != null,
  hasOwner: (state) =>
    getters.hasCustomer(state) ||
    getters.hasCustomerCompany(state) ||
    getters.hasLeaseCompany(state),
  hasFleetSalesContract: (state) =>
    state.quote?.QuoteDetails?.FleetSalesContract?.ContractNumber?.length > 0,
  hasCustomerType: (state) => {
    return getters.customerType(state)?.Id > 0
  },
  hasChildQuotes: (state) => state.quote?.ChildQuotes && state.quote?.ChildQuotes.length,

  bpmCalculated: (state) => state.quote?.QuoteDetails?.BpmCalculated,

  employee: (state) => state.quote?.QuoteDetails?.Employee,
  dealer: (state) => state.quote?.QuoteDetails?.Dealer,
  dealerZipcode: (state) => state.quote?.QuoteDetails?.Dealer?.DealerZipCode,

  latestVwpfsCall: (state) => state.quote?.LatestVwpfsCall,
  latestVwpfsPrivateLeaseCall: (state) => state.quote?.LatestVwpfsPrivateLeaseCall,
  latestVwpfsOperationalLeaseCall: (state) => state.quote?.LatestVwpfsOperationalLeaseCall,
  latestVwpfsInsuranceCall: (state) => state.quote?.LatestVwpfsInsuranceCall,
  expectedDeliveryDates: (state) => state.quote?.DeliveryDates,
  doNotDeliverBeforeDate: (state) => state.quote?.QuoteDetails?.DoNotDeliverBeforeDate,
  deliverAsSoonAsPossible: (state) => state.quote?.QuoteDetails?.DeliverAsSoonAsPossible,

  dealerOrder: (state) => state.quote?.QuoteDetails?.AddendumTexts?.DealerOrder,
  importerOrder: (state) => state.quote?.QuoteDetails?.AddendumTexts?.ImporterOrder,

  // prices
  totalPriceExclBtw: (state, getters, rootstate) => {
    const configuration = rootstate.configuration
    const exchangeCars = getters.exchangeCars
    const accessories = getters.accessories
    const controlCodes = getters.controlCodes
    return functions.calculateTotalPriceExclBtw(
      configuration,
      exchangeCars,
      accessories,
      controlCodes
    )
  },
  totalPriceInclBtw: (state, getters, rootstate) => {
    const configuration = rootstate.configuration
    const exchangeCars = getters.exchangeCars
    const accessories = getters.accessories
    const controlCodes = getters.controlCodes
    return functions.calculateTotalPriceInclBtw(
      configuration,
      exchangeCars,
      accessories,
      controlCodes
    )
  },
  totalPriceExclBtwInclBpm: (state, getters, rootstate) => {
    const configuration = rootstate.configuration
    const exchangeCars = getters.exchangeCars
    const accessories = getters.accessories
    const controlCodes = getters.controlCodes
    return functions.calculateTotalPriceExclBtwInclBpm(
      configuration,
      exchangeCars,
      accessories,
      controlCodes
    )
  },
  validationMessages: (state) => state.quote?.Metadata?.ValidationMessages || [],
  tempLatestVwpfsCalls: (state) => state.tempLatestVwpfsCalls,
  komNumber: (state) => state.car?.KomNumber?.Number,
  komYear: (state) => state.car?.KomNumber?.Year,
  hasKomNumber: (state) =>
    !getters.isStockCar(state) && getters.komNumber(state) && getters.komYear(state),
}

const mutations = {
  /* eslint-disable no-param-reassign */
  setQuote: (state, payload) => {
    payload.LatestVwpfsInsuranceCall = ObjectHelper.keysToCamelCase(
      payload.LatestVwpfsInsuranceCall
    )
    state.quote = payload
    emitStoreEvents()
  },
  setPrevQuote: (state, payload) => {
    state.prevQuote = payload
  },
  addChildQuote: (state, payload) => {
    if (!state.quote.ChildQuotes) {
      state.quote.ChildQuotes = []
    }
    var status = payload.Status.split('_')[1]
    state.quote.ChildQuotes.push({
      CreatedOn: payload?.Metadata.CreatedOn,
      QuoteId: payload.QuoteId,
      FormattedStatus: status || 'QUOTE',
    })
  },
  updateChildQuoteStatus(state, payload) {
    const formattedStatus = payload.status.split('_')[1] || 'QUOTE'

    const childQuoteToUpdate = state.quote.ChildQuotes?.find((x) => x.QuoteId == payload.id)
    childQuoteToUpdate.FormattedStatus = formattedStatus
  },
  setSigningStatus: (state, payload) => {
    state.quote.SigningStatus = payload
  },
  setCalculateBpm: (state, payload) => {
    state.quote.QuoteDetails.BpmCalculated = payload
  },
  setExpireDate: (state, payload) => {
    state.quote.QuoteDetails.ExpireDate = payload
    EventBus.$emit('quote/expireDate')
  },
  setDeliveryDate: (state, payload) => {
    state.quote.QuoteDetails.DeliveryDate = payload
    emitStoreEvents()
    EventBus.$emit('quote/updateConfiguration')
  },
  setDeliveryDateInitial: (state, payload) => {
    state.quote.QuoteDetails.DeliveryDate = payload
    emitStoreEvents()
    EventBus.$emit('quote/updateConfiguration')
  },
  setDoNotDeliverBeforeDate: (state, payload) => {
    if (!payload) {
      state.quote.QuoteDetails.DoNotDeliverBeforeDate = null
      state.quote.QuoteDetails.DeliverAsSoonAsPossible = true
    } else {
      state.quote.QuoteDetails.DoNotDeliverBeforeDate = payload
      state.quote.QuoteDetails.DeliverAsSoonAsPossible = false
    }
    emitStoreEvents()
  },
  setAccessories: (state, payload) => {
    state.quote.QuoteDetails.DealerAccessories = payload
    emitStoreEvents()
  },
  addToAccessories: (state, payload) => {
    state.quote.QuoteDetails.DealerAccessories.push(payload)
    emitStoreEvents()
  },
  removeFromControlCodes: (state, payload) => {
    state.quote.QuoteDetails.ControlCodes = state.quote.QuoteDetails.ControlCodes.filter(
      (c) => c.Id !== payload.Id
    )
    emitStoreEvents()
  },
  addToControlCodes: (state, payload) => {
    state.quote.QuoteDetails.ControlCodes.push(payload)
    emitStoreEvents()
  },
  removeFromAccessories: (state, payload) => {
    state.quote.QuoteDetails.DealerAccessories = state.quote.QuoteDetails.DealerAccessories.filter(
      (p) => !isEqualProducts(payload, p)
    )
    emitStoreEvents()
  },
  resetAccessoryAllWeatherTyresPL: (state) => {
    let accessoriesAllWeatherTyresPL = state.quote.QuoteDetails.DealerAccessories.filter(
      (p) => p.AllWeatherTyresPL
    )
    accessoriesAllWeatherTyresPL.forEach((a) => (a.AllWeatherTyresPL = false))
    emitStoreEvents()
  },
  modifyAccessory: (state, payload) => {
    const index = state.quote.QuoteDetails.DealerAccessories.findIndex((p) =>
      isEqualProducts(payload, p)
    )

    state.quote.QuoteDetails.DealerAccessories[index] = payload
    EventBus.$emit('quote/modifyAccessory', payload?.Category)
  },
  loadingAccessory: (state, payload) => {
    const accessory = state.quote.QuoteDetails.DealerAccessories[payload]
    if (accessory) accessory.Loading = true
    //Note that after a save/previewsave the loading property will be undefined
  },
  setCustomer: (state, payload) => {
    state.quote.QuoteDetails.Customer = payload
    emitStoreEvents()
  },
  setCustomerCompany: (state, payload) => {
    state.quote.QuoteDetails.CustomerCompany = payload
    emitStoreEvents()
  },
  setLeaseCompany: (state, payload) => {
    state.quote.QuoteDetails.LeaseCompany = payload
    emitStoreEvents()
  },
  setFleetsalesContract: (state, payload) => {
    state.quote.QuoteDetails.FleetSalesContract = payload
    EventBus.$emit('quote/fleetContract')
  },
  setCar: (state, payload) => {
    state.car = payload
    state.quote.QuoteDetails.Cars[0] = payload
    emitStoreEvents()
  },
  setCarInitial: (state, payload) => {
    state.car = payload
    state.quote.QuoteDetails.Cars[0] = payload
    emitStoreEvents()
  },
  setConfigurationCode: (state, payload) => {
    state.quote.QuoteDetails.Cars[0].ConfigurationCode = payload
    state.car.ConfigurationCode = payload
    EventBus.$emit('quote/updateConfiguration')
  },
  setConfigurationCodeInitial: (state, payload) => {
    state.quote.QuoteDetails.Cars[0].ConfigurationCode = payload
    state.car.ConfigurationCode = payload
  },
  setStockCarIdInitial: (state, payload) => {
    state.quote.QuoteDetails.Cars[0].StockCarId = payload
    state.car.StockCarId = payload
  },
  setStockCarId: (state, payload) => {
    state.quote.QuoteDetails.Cars[0].StockCarId = payload
    state.car.StockCarId = payload
    EventBus.$emit('quote/updateConfiguration')
  },
  setStockId: (state, payload) => {
    state.quote.QuoteDetails.Cars[0].StockId = payload
    state.car.StockId = payload
    EventBus.$emit('quote/updateConfiguration')
  },
  setEmployee: (state, payload) => {
    state.quote.QuoteDetails.Employee = payload
    emitStoreEvents()
  },
  setRemark: (state, payload) => {
    state.quote.QuoteDetails.Remark = payload
    emitStoreEvents()
  },
  setEmailBodyQuote: (state, payload) => {
    state.quote.QuoteDetails.EmailBodyQuote = payload
    emitStoreEvents()
  },
  setEmailBodySalesOrder: (state, payload) => {
    state.quote.QuoteDetails.EmailBodySalesOrder = payload
    emitStoreEvents()
  },
  setExchangeCars: (state, payload) => {
    // Ensure payload is an array
    state.quote.QuoteDetails.ExchangeCars = Array.isArray(payload) ? payload : []
    EventBus.$emit('quote/exchangeCars')
    EventBus.$emit('quote/hasExchangeCars')
    emitStoreEvents()
  },
  addExchangeCar: (state, payload) => {
    if (!state.quote.QuoteDetails.ExchangeCars) {
      state.quote.QuoteDetails.ExchangeCars = []
    }

    // Max 3 cars
    if (state.quote.QuoteDetails.ExchangeCars.length < 3) {
      state.quote.QuoteDetails.ExchangeCars.push(payload)
      EventBus.$emit('quote/exchangeCars')
      EventBus.$emit('quote/hasExchangeCars')
      emitStoreEvents()
    }
  },
  removeExchangeCar: (state, index) => {
    if (
      state.quote.QuoteDetails.ExchangeCars &&
      index >= 0 &&
      index < state.quote.QuoteDetails.ExchangeCars.length
    ) {
      state.quote.QuoteDetails.ExchangeCars.splice(index, 1)
      EventBus.$emit('quote/exchangeCars')
      EventBus.$emit('quote/hasExchangeCars')
      emitStoreEvents()
    }
  },
  updateExchangeCar: (state, { index, car }) => {
    if (
      state.quote.QuoteDetails.ExchangeCars &&
      index >= 0 &&
      index < state.quote.QuoteDetails.ExchangeCars.length
    ) {
      state.quote.QuoteDetails.ExchangeCars[index] = car
      EventBus.$emit('quote/exchangeCars')
      EventBus.$emit('quote/hasExchangeCars')
      emitStoreEvents()
    }
  },
  setControlCodes: (state, payload) => {
    state.quote.QuoteDetails.ControlCodes = payload
    emitStoreEvents()
  },
  setIsStockOrder: (state, payload) => {
    state.quote.QuoteDetails.IsStockOrder = payload
    emitStoreEvents()
  },
  setLoading: (state, payload) => {
    state.loading = payload
    EventBus.$emit('quote/loading')
  },
  setIsSaving: (state, payload) => {
    state.isSaving = payload
    EventBus.$emit('quote/isSaving')
  },
  setIsCreating: (state, payload) => {
    state.isCreating = payload
    EventBus.$emit('quote/isCreating')
  },
  setShouldUpdateEmployee: (state, payload) => {
    state.shouldUpdateEmployee = payload
    emitStoreEvents()
  },
  setShouldUpdateEstablishment: (state, payload) => {
    state.shouldUpdateEstablishment = payload
    emitStoreEvents()
  },
  setShouldCreateMatchingStockQuote: (state, payload) => {
    state.shouldCreateMatchingStockQuote = payload
  },
  setCreateMatchingStockQuoteOptions: (state, payload) => {
    state.createMatchingStockQuoteOptions = payload
  },
  setEstablishment: (state, payload) => {
    state.quote.QuoteDetails.Dealer = payload
    emitStoreEvents()
  },
  setCustomerType: (state, customerType) => {
    state.quote.QuoteDetails.CustomerType = customerType
    EventBus.$emit('quote/customerType')
  },
  setLeaseReference: (state, payload) => {
    state.quote.QuoteDetails.LeaseReference = payload
    EventBus.$emit('quote/leaseReference')
  },
  setPriceDisplayMode: (state, payload) => {
    state.quote.QuoteDetails.PriceDisplayMode = payload
    EventBus.$emit('quote/priceDisplayMode')
  },
  setShowVwpfsFinancePdf: (state, payload) => {
    state.quote.QuoteDetails.ShowVwpfsFinancePdf = payload
    EventBus.$emit('quote/showVwpfsFinancePdf')
  },
  setShowVwpfsPrivateLeasePdf: (state, payload) => {
    state.quote.QuoteDetails.ShowVwpfsPrivateLeasePdf = payload
    EventBus.$emit('quote/showVwpfsPrivateLeasePdf')
  },
  setShowVwpfsInsurancePdf: (state, payload) => {
    state.quote.QuoteDetails.ShowVwpfsInsurancePdf = payload
    EventBus.$emit('quote/showVwpfsInsurancePdf')
  },
  setShowVwpfsOperationalLeasePdf: (state, payload) => {
    state.quote.QuoteDetails.ShowVwpfsOperationalLeasePdf = payload
    EventBus.$emit('quote/showVwpfsOperationalLeasePdf')
  },
  setDefaultVwpfsInsurancePdf: (state, payload) => {
    state.quote.QuoteDetails.ShowVwpfsInsurancePdf = payload
    EventBus.$emit('quote/defaultVwpfsInsurancePdf')
  },
  setTempLatestVwpfsCalls: (state, payload) => {
    state.tempLatestVwpfsCalls[payload.type] = payload.value
  },
  setPlQuotationId: (state, payload) => {
    state.quote.QuoteDetails.PlQuotationId = payload
    EventBus.$emit('quote/setPlQuotationId')
  },
  setStockButtonDate: (state, payload) => {
    state.quote.QuoteDetails.StockButtonDate = payload
    EventBus.$emit('quote/setStockButtonDate')
  },

  setLatestVwpfsCall: (state, payload) => {
    if (!payload) {
      state.quote.LatestVwpfsCall = null
    } else {
      const calculationResult = payload?.result
      const calculationInput = payload?.input
      if (!calculationInput && !calculationResult) {
        state.quote.LatestVwpfsCall = payload
      } else {
        state.quote.LatestVwpfsCall = {
          CreatedOn: calculationResult.responseTimeStamp,
          Brand: calculationInput.objectBrand,
          Duration: calculationInput.duration,
          DownPayment: calculationInput.downPayment,
          BalloonPayment: calculationInput.balloonPayment,
          Channel: calculationInput.channel,
          ProductType: calculationInput.productType,
          ObjectType: calculationInput.objectType,
          InterestRateEffective: calculationResult.effectiveAnnualInterestRate,
          InterestRateNominal: calculationResult.interestRate,
          MonthPrice: calculationResult.ratePerMonth,
          NetCreditAmount: calculationResult.netCreditAmount,
          GrossCreditAmount: calculationResult.grossCreditAmount,
          ObjectPriceExVatExBpm: calculationInput.objectPriceExVATExBPM,
          ObjectPriceInclVatInclBpm: calculationInput.objectPriceInclVATInclBPM,
          FinalRate: calculationResult.finalRate,
          Warnings: payload.warnings,
          Errors: payload.errors,
        }
      }
    }
    EventBus.$emit('quote/latestVwpfsCall')
  },

  setLatestVwpfsPrivateLeaseCall: (state, payload) => {
    if (!payload) {
      state.quote.LatestVwpfsPrivateLeaseCall = null
    } else {
      state.quote.LatestVwpfsPrivateLeaseCall = payload
    }
    EventBus.$emit('quote/latestVwpfsPrivateLeaseCall')
  },
  setLatestVwpfsOperationalLeaseCall: (state, payload) => {
    if (!payload) {
      state.quote.LatestVwpfsOperationalLeaseCall = null
    } else {
      state.quote.LatestVwpfsOperationalLeaseCall = payload
    }
    EventBus.$emit('quote/latestVwpfsOperationalLeaseCall')
  },
  setLatestVwpfsInsuranceCall: (state, payload) => {
    if (!payload) {
      state.quote.LatestVwpfsInsuranceCall = null
    } else {
      state.quote.LatestVwpfsInsuranceCall = payload
    }
    EventBus.$emit('quote/latestVwpfsInsuranceCall')
  },
  setExpectedDeliveryDates: (state, payload) => {
    if (!payload) {
      state.quote.DeliveryDates = null
    } else {
      state.quote.DeliveryDates = payload
    }
  },
  clearTempLatestVwpfsCalls: (state, payload) => {
    state.tempLatestVwpfsCalls[payload] = null
  },
}

// The signed-in employee for a new quote: PCQM only sees a client-credentials token and cannot look
// the user up, so the employee travels in the body (CST-4514). PCQM fills in everything else.
function signedInEmployee(rootGetters) {
  return {
    PonId: rootGetters['user/userName'],
    AutolineId: rootGetters['user/autolineId'],
    EmployeeName: rootGetters['user/name'],
    EmployeeEmail: rootGetters['user/email'],
  }
}

const actions = {
  setQuote: ({ commit, state }, payload) => {
    if (Array.isArray(payload.QuoteDetails.DealerAccessories)) {
      payload.QuoteDetails.DealerAccessories = addIndex(payload.QuoteDetails.DealerAccessories)
    } else {
      payload.QuoteDetails.DealerAccessories = []
    }

    commit('setQuote', payload)
    commit('setCustomerType', payload.QuoteDetails?.CustomerType)
    commit('printOptions/setContractor', payload.QuoteDetails?.PrintOptions?.PrintContractorOnOrder)
    commit('printOptions/setContactInfo', payload.QuoteDetails?.PrintOptions?.PrintContactInfoOnPdf)

    // actual data
    if (payload.QuoteDetails?.Cars.length > 0) {
      commit('setCarInitial', payload.QuoteDetails.Cars[0])
    }
  },
  resetQuote: ({ commit, dispatch }, payload) => {
    let emptyQuote = {
      QuoteDetails: {
        Cars: [],
        DealerAccessories: [],
        ExchangeCars: [],
        ControlCodes: [],
        LeaseReference: '',
        PriceDisplayMode: null,
        FleetSalesContract: {
          ContractNumber: null,
          DiscountPercentage: null,
          Remarks: null,
          OrderDate: null,
          SelectedDate: null,
        },
        DeliveryDate: null,
        DoNotDeliverBeforeDate: null,
        DeliverAsSoonAsPossible: true,
        IsStockOrder: false,
        Customer: null,
        CustomerCompany: null,
        Dealer: {
          EstablishmentID: '',
        },
        CreatedBySource: '',
        ShoppingCartId: null,
      },
      LatestVwpfsCall: null,
      LatestVwpfsPrivateLeaseCall: null,
      LatestVwpfsOperationalLeaseCall: null,
      LatestVwpfsInsuranceCall: null,
      BpmCalculated: true,
      DeliveryDates: null,
    }

    let customer = {}
    dispatch('setQuote', emptyQuote)
    dispatch('setCustomer', customer)
  },
  loadQuote: ({ commit }, loadedQuote) => {
    if (Array.isArray(loadedQuote.QuoteDetails.DealerAccessories)) {
      loadedQuote.QuoteDetails.DealerAccessories = addIndex(
        loadedQuote.QuoteDetails.DealerAccessories
      )
    } else {
      loadedQuote.QuoteDetails.DealerAccessories = []
    }

    commit('setQuote', loadedQuote)
    commit('setCustomerType', loadedQuote.QuoteDetails?.CustomerType)
    commit(
      'printOptions/setContractor',
      loadedQuote.QuoteDetails?.PrintOptions?.PrintContractorOnOrder
    )
    commit(
      'printOptions/setContactInfo',
      loadedQuote.QuoteDetails?.PrintOptions?.PrintContactInfoOnPdf
    )
    commit('setCar', loadedQuote.QuoteDetails.Cars[0])

    return
  },
  addChildQuote: ({ commit }, payload) => {
    commit('addChildQuote', payload)
  },
  setExpireDate: ({ commit, dispatch }, payload) => {
    commit('setExpireDate', payload)
    dispatch('previewSave')
  },
  setDeliveryDate: ({ commit }, payload) => {
    commit('setDeliveryDate', payload)
  },
  setDoNotDeliverBeforeDate: ({ commit, dispatch }, payload) => {
    commit('setDoNotDeliverBeforeDate', payload)
    dispatch('previewSave')
  },
  setAccessories: ({ commit }, payload) => {
    if (Array.isArray(payload)) {
      // The Pon catalog enrichment is display-only and must not be saved on the quote
      var accessories = addIndex(payload.map(stripEnrichment))
    } else {
      accessories = []
    }

    commit('setAccessories', accessories)
    return Promise.resolve(true)
  },
  setControlCodes: ({ commit }, payload) => {
    commit('setControlCodes', payload)
  },
  addToAccessories: ({ state, commit, dispatch, getters }, payload) => {
    const accessory = stripEnrichment(payload)
    accessory.Index = getters.accessories.length

    if (isProductInSelectedProducts(state, accessory)) return

    commit('addToAccessories', accessory)
    dispatch('previewSaveAccessory', accessory.Index)
  },
  addControlCode: ({ commit, dispatch }, payload) => {
    commit('addToControlCodes', payload)
    dispatch('previewSaveAccessory')
  },
  removeFromControlCodes: ({ commit, dispatch }, payload) => {
    commit('removeFromControlCodes', payload)
    dispatch('previewSaveAccessory')
  },
  removeFromAccessories: ({ commit, dispatch }, payload) => {
    commit('removeFromAccessories', payload)
    dispatch('previewSaveAccessory')
  },
  resetAccessoryAllWeatherTyresPL: ({ commit, dispatch }, payload) => {
    commit('resetAccessoryAllWeatherTyresPL')
    if (payload) {
      dispatch('previewSave')
    }
  },
  modifyOrAddAccessory: ({ commit, dispatch, getters }, payload) => {
    const accessory = stripEnrichment(payload)
    if (accessory.OptionType_ID) {
      commit('modifyAccessory', accessory)
    } else {
      accessory.Index = getters.accessories.length
      commit('addToAccessories', accessory)
    }

    dispatch('previewSaveAccessory', accessory.Index)
  },
  modifyAccessory: ({ commit, dispatch }, payload) => {
    const accessory = stripEnrichment(payload)
    commit('modifyAccessory', accessory)
    dispatch('previewSaveAccessory', accessory.Index)
  },
  setIsStockOrder: ({ commit }, payload) => {
    commit('setIsStockOrder', payload)
  },
  setStockOrder: async ({ commit, dispatch, getters }, checked) => {
    commit('setCustomer', null)

    if (checked) {
      if (getters.hasCustomerCompany) await dispatch('resetCustomerCompany')
      if (getters.hasLeaseCompany) await dispatch('resetLeaseCompany')
    }

    commit('setIsStockOrder', checked)
    await dispatch('createOrUpdate')
  },
  setCustomer: ({ commit }, payload) => {
    commit('setCustomer', payload)
  },
  setCustomerCompanyWithoutSaving: ({ commit }, payload) => {
    var customerCompany = payload ? payload : null
    commit('setCustomerCompany', customerCompany)
  },
  setCustomerCompany: async ({ commit, dispatch, getters }, payload) => {
    var customerCompany = payload ? payload : null

    commit('setCustomerCompany', customerCompany)

    try {
      await Customers.addCustomerCompany(
        getters.quoteId,
        getCustomerPoId(customerCompany),
        customerCompany.CustomerCompanySource
      )
    } catch (error) {
      console.error('Failed to add customer company:', error)
      // Revert the commit if API call fails
      commit('setCustomerCompany', null)
      throw error
    }
  },
  resetCustomerCompany: async ({ commit, dispatch, getters }) => {
    const quoteId = getters.quoteId

    // If we have a quote ID, use the new remove API
    if (quoteId) {
      try {
        await Customers.removeCompany(quoteId)
      } catch (error) {
        console.error('Failed to remove customer company:', error)
        throw error
      }
    }

    commit('setCustomerCompany', {})
  },
  setLeaseCompany: async ({ commit, dispatch, getters }, payload) => {
    var leaseCompany = payload ? payload : null

    commit('setLeaseCompany', leaseCompany)

    try {
      await Customers.addLeaseCompany(
        getters.quoteId,
        getCompanyReferenceNumber(leaseCompany),
        leaseCompany.CustomerCompanySource
      )
    } catch (error) {
      console.error('Failed to add lease company:', error)
      // Revert the commit if API call fails
      commit('setLeaseCompany', null)
      throw error
    }
  },
  setLeaseReference: async ({ commit, dispatch, getters }, payload) => {
    var leaseReference = payload ? payload : null
    try {
      const result = await Customers.addLeaseReference(getters.quoteId, leaseReference)
      commit('setLeaseReference', leaseReference)
      return result
    } catch (error) {
      throw error
    }
  },
  resetLeaseCompany: async ({ commit, dispatch, getters }) => {
    const quoteId = getters.quoteId

    try {
      await Customers.removeLeaseCompany(quoteId)
    } catch (error) {
      console.error('Failed to remove lease company:', error)
      throw error
    }

    commit('setLeaseCompany', null)
  },
  resetLeaseReference: async ({ commit, dispatch, getters }) => {
    const quoteId = getters.quoteId

    try {
      await Customers.removeLeaseReference(quoteId)
    } catch (error) {
      console.error('Failed to remove lease reference:', error)
      throw error
    }

    commit('setLeaseReference', null)
  },
  setFleetContract: ({ commit }, payload) => {
    var contract = typeof payload === 'string' ? { ContractNumber: payload } : payload
    commit('setFleetsalesContract', contract)
  },
  resetFleetContract: ({ commit }) => {
    commit('setFleetsalesContract', null)
  },
  setCar: ({ commit }, payload) => {
    commit('setCar', payload)
  },
  setCarInitial: ({ commit }, payload) => {
    commit('setCarInitial', payload)
  },
  setConfigurationCode: ({ commit }, payload) => {
    commit('setConfigurationCode', payload)
  },
  setStockCarId: ({ commit, dispatch }, payload) => {
    commit('setStockCarId', payload)
  },
  setStockId: ({ commit, dispatch }, payload) => {
    commit('setStockId', payload)
  },
  async setEmployee({ commit, dispatch }, payload) {
    commit('setEmployee', payload)
    dispatch('setShouldUpdateEmployee', true)

    return Promise.resolve()
  },
  setRemark: ({ commit }, payload) => {
    commit('setRemark', payload)
  },
  setEmailBodyQuote: ({ commit }, payload) => {
    commit('setEmailBodyQuote', payload)
  },
  setEmailBodySalesOrder: ({ commit }, payload) => {
    commit('setEmailBodySalesOrder', payload)
  },
  setExchangeCars: ({ commit }, payload) => {
    // Normalize all cars in the array
    const normalizedCars = payload.map((car) => {
      const normalizedCar = Object.assign({}, car)
      normalizedCar.Price = car.Price || {}
      normalizedCar.Bpm = car.Bpm || { Value: 0 }
      return normalizedCar
    })

    commit('setExchangeCars', normalizedCars)
  },
  addExchangeCar: ({ commit }, payload) => {
    const car = Object.assign({}, payload)
    car.Price = payload.Price || {}
    car.Bpm = payload.Bpm || { Value: 0 }
    commit('addExchangeCar', car)
  },
  removeExchangeCar: ({ commit }, index) => {
    commit('removeExchangeCar', index)
  },
  updateExchangeCar: ({ commit }, { index, car }) => {
    const normalizedCar = Object.assign({}, car)
    normalizedCar.Price = car.Price || {}
    normalizedCar.Bpm = car.Bpm || { Value: 0 }
    commit('updateExchangeCar', { index, car: normalizedCar })
  },
  setLoading: ({ commit }, payload) => {
    commit('setLoading', payload)
  },
  setIsSaving: ({ commit }, payload) => {
    commit('setIsSaving', payload)
  },
  // The print options and the quote are the same PCQM row. PCQM's quote update loads the quote and
  // later saves it whole, so a print-options save that commits in between is overwritten (a lost
  // update: after creating a quote, printContractorOnOrder ended up NULL -- CST-4514). Run them one
  // at a time through the isSaving flag that previewSave and update already respect: wait for a
  // running quote save, and hold the flag while saving, so those wait for this one.
  savePrintOptions: async ({ dispatch, state }, pdfOptions) => {
    const waitedSince = Date.now()
    while (state.isSaving && Date.now() - waitedSince < PRINT_OPTIONS_MAX_WAIT_MS) {
      await new Promise((resolve) => setTimeout(resolve, 100))
    }

    dispatch('setIsSaving', true)
    try {
      return await PrintOptionsApi.setPdfOptions(pdfOptions)
    } finally {
      dispatch('setIsSaving', false)
    }
  },
  setIsCreating: ({ commit }, payload) => {
    commit('setIsCreating', payload)
  },
  createOrUpdate: ({ dispatch, state }) => {
    const isNew = !state.quote.QuoteId || state.quote.QuoteId.length === 0
    return isNew ? dispatch('create') : dispatch('update')
  },
  setShouldUpdateEmployee: ({ commit }, payload) => {
    commit('setShouldUpdateEmployee', payload)
  },
  setShouldUpdateEstablishment: ({ commit }, payload) => {
    commit('setShouldUpdateEstablishment', payload)
  },
  setEstablishment: ({ commit }, payload) => {
    const establishment = {
      DealerName: payload.Name,
      DealerEmail: payload.Email,
      DealerPhone: payload.Telephone,
      DealerAddress: payload.Address,
      DealerZipCode: payload.Zipcode,
      DealerCity: payload.City,
      EstablishmentID: payload.EstablishmentNumber,
    }

    commit('setEstablishment', establishment)
  },
  setCustomerTypeId: ({ commit, dispatch }, payload) => {
    const customerType = { Id: payload }

    commit('setCustomerType', customerType)
    dispatch('updateCustomerType', customerType)
  },
  setPriceDisplayMode: ({ commit, dispatch }, payload) => {
    commit('setPriceDisplayMode', payload)
    dispatch('previewSave')
  },
  setShowVwpfsFinancePdf: ({ commit }, payload) => {
    commit('setShowVwpfsFinancePdf', payload)
  },
  setShowVwpfsPrivateLeasePdf: ({ commit }, payload) => {
    commit('setShowVwpfsPrivateLeasePdf', payload)
  },
  setShowVwpfsInsurancePdf: ({ commit }, payload) => {
    commit('setShowVwpfsInsurancePdf', payload)
  },
  setShowVwpfsOperationalLeasePdf: ({ commit }, payload) => {
    commit('setShowVwpfsOperationalLeasePdf', payload)
  },
  setDefaultVwpfsInsurancePdf: ({ commit }, payload) => {
    commit('setDefaultVwpfsInsurancePdf', payload)
  },
  setLatestVwpfsCall: ({ commit }, payload) => {
    commit('setLatestVwpfsCall', payload)
  },
  setLatestVwpfsPrivateLeaseCall: ({ commit }, payload) => {
    commit('setLatestVwpfsPrivateLeaseCall', payload)
  },
  setLatestVwpfsOperationalLeaseCall: ({ commit }, payload) => {
    commit('setLatestVwpfsOperationalLeaseCall', payload)
  },
  setLatestVwpfsInsuranceCall: ({ commit }, payload) => {
    commit('setLatestVwpfsInsuranceCall', payload)
  },
  setExpectedDeliveryDates: ({ commit }, payload) => {
    commit('setExpectedDeliveryDates', payload)
  },
  setPlQuotationId: ({ commit, dispatch }, payload) => {
    commit('setPlQuotationId', payload)
    dispatch('previewSave')
  },
  setStockButtonDate: ({ commit }, payload) => {
    commit('setStockButtonDate', payload)
  },
  //API calls

  updateCustomerType: ({ dispatch, state }, customerType) => {
    return Quote.updateCustomerType(state.quote.QuoteId, customerType.Id)
  },

  previewSaveAccessory: ({ commit, dispatch }, payload) => {
    commit('loadingAccessory', payload)
    return dispatch('previewSave')
  },

  previewSave: ({ dispatch, state }, payload) => {
    state.previewSaveLatestFunction = () => dispatch('previewSaveInternal', payload)

    clearTimeout(state.previewSaveTimeout)
    state.previewSaveTimeout = null

    if (state.isSaving) {
      state.previewSaveTimeout = setTimeout(state.previewSaveLatestFunction, 5000)
    } else {
      return state.previewSaveLatestFunction()
    }
  },

  previewSaveInternal: ({ dispatch, state }) => {
    dispatch('setIsSaving', true)
    return Quote.previewSave(state.quote)
      .then((response) => {
        if (state.previewSaveTimeout) {
          dispatch('previewSaveCallFromTimeout')
          return
        } else {
          //Always keep the old quoteId. Prevents duplicate quotes
          response.QuoteId = state.quote.QuoteId
          dispatch('setQuote', response)
          dispatch('setIsSaving', false)
          return Promise.resolve(response)
        }
      })
      .catch((e) => {
        dispatch('setIsSaving', false)
        if (axios.isCancel(e)) {
          return
        }
      })
  },

  previewSaveCallFromTimeout: ({ state }) => {
    clearTimeout(state.previewSaveTimeout)
    state.previewSaveTimeout = null
    state.previewSaveLatestFunction()
  },

  create: ({ dispatch, state, rootGetters }) => {
    dispatch('setIsCreating', true)
    dispatch('setLoading', true)
    return Quote.create(state.quote.QuoteDetails, signedInEmployee(rootGetters))
      .then((response) => {
        dispatch('setQuote', response.data)

        dispatch('setShouldUpdateEmployee', false)
        dispatch('setShouldUpdateEstablishment', false)

        EventBus.$emit('transitionToEditState')
        return Promise.resolve(response)
      })
      .finally(() => {
        dispatch('setIsCreating', false)
        dispatch('setLoading', false)
      })
  },

  createFromCartId: ({ dispatch, state, rootGetters }, payload) => {
    dispatch('setIsCreating', true)
    dispatch('setLoading', true)
    return Quote.createFromCartId(state.quote.QuoteDetails, payload, signedInEmployee(rootGetters))
      .then((response) => {
        dispatch('setQuote', response.data)

        dispatch('setShouldUpdateEmployee', false)
        dispatch('setShouldUpdateEstablishment', false)

        EventBus.$emit('transitionToEditState')
        return Promise.resolve(response)
      })
      .finally(() => {
        dispatch('setIsCreating', false)
        dispatch('setLoading', false)
      })
  },
  createForNewStockCart: (
    { dispatch, state, rootGetters },
    { commissionNumber, commissionYear }
  ) => {
    const employee = signedInEmployee(rootGetters)
    return Quote.createForNewStockCar(
      state.quote.QuoteDetails,
      commissionNumber,
      commissionYear,
      employee
    )
      .then((response) => {
        dispatch('setQuote', response.data)

        dispatch('setShouldUpdateEmployee', false)
        dispatch('setShouldUpdateEstablishment', false)

        EventBus.$emit('transitionToEditState')
        return Promise.resolve(response)
      })
      .finally(() => {
        dispatch('setIsCreating', false)
        dispatch('setLoading', false)
      })
  },

  update: ({ dispatch, state }, payload) => {
    state.updateLatestFunction = () => dispatch('updateInternal', payload)

    clearTimeout(state.updateTimeout)
    state.updateTimeout = null

    if (state.isSaving) {
      state.updateTimeout = setTimeout(state.updateLatestFunction, 5000)
    } else {
      return state.updateLatestFunction()
    }
  },

  updateInternal: ({ dispatch, state }, payload) => {
    const options = Object.assign(
      {
        shouldUpdateEmployee: state.shouldUpdateEmployee,
        shouldUpdateEstablishment: state.shouldUpdateEstablishment,
      },
      payload
    )

    dispatch('setIsSaving', true)
    dispatch('setLoading', true)
    return Quote.update(state.quote, options)
      .then((response) => {
        if (state.updateTimeout) {
          dispatch('updateCallFromTimeout')
          return
        } else {
          //Always keep the old quoteId. Prevents duplicate quotes
          response.data.QuoteId = state.quote.QuoteId
          dispatch('setQuote', response.data)

          dispatch('setShouldUpdateEmployee', false)
          dispatch('setShouldUpdateEstablishment', false)
          dispatch('setIsSaving', false)
          dispatch('setLoading', false)

          return Promise.resolve(response)
        }
      })
      .catch((e) => {
        dispatch('setIsSaving', false)
        dispatch('setLoading', false)
        if (!axios.isCancel(e)) {
          return
        }

        throw e
      })
  },

  updateCallFromTimeout: ({ state }) => {
    clearTimeout(state.updateTimeout)
    state.updateTimeout = null
    state.updateLatestFunction()
  },
}

const functions = {
  calculateTotalPriceExclBtw(configuration, exchangeCars, accessories, controlCodes) {
    let configPriceExcl = 0.0

    if (configuration !== null && configuration.price !== null) {
      configPriceExcl = parseFloat(configuration.price.Excl)
    }

    // Sum up all exchange car prices
    let exchangeCarsPrice = 0
    if (Array.isArray(exchangeCars)) {
      exchangeCarsPrice = exchangeCars
        .filter((car) => car?.Price?.Excl)
        .map((car) => parseFloat(car.Price.Excl))
        .reduce((sum, price) => sum + price, 0)
    }

    let dealerExtrasTotalExcl = accessories
      .map((x) => parseFloat(x.Price.Excl || 0))
      .reduce(arrayFunctions.sum, 0)
    let controlCodesTotalExcl = controlCodes
      .map((x) => parseFloat(x.Price.Excl) || 0)
      .reduce(arrayFunctions.sum, 0)

    return configPriceExcl + dealerExtrasTotalExcl + controlCodesTotalExcl - exchangeCarsPrice
  },
  calculateTotalPriceInclBtw(configuration, exchangeCars, accessories, controlCodes) {
    let configPriceIncl = 0.0

    if (configuration !== null && configuration.price !== null) {
      configPriceIncl = parseFloat(configuration.price.Incl)
    }

    // Sum up all exchange car prices
    let exchangeCarsPrice = 0
    if (Array.isArray(exchangeCars)) {
      exchangeCarsPrice = exchangeCars
        .filter((car) => car?.Price?.Incl)
        .map((car) => parseFloat(car.Price.Incl))
        .reduce((sum, price) => sum + price, 0)
    }

    let dealerExtrasTotalIncl = accessories
      .map((x) => parseFloat(x.Price.Incl || 0))
      .reduce(arrayFunctions.sum, 0)
    let controlCodesTotalIncl = controlCodes
      .map((x) => parseFloat(x.Price.Incl) || 0)
      .reduce(arrayFunctions.sum, 0)

    return configPriceIncl + dealerExtrasTotalIncl + controlCodesTotalIncl - exchangeCarsPrice
  },
  calculateTotalPriceExclBtwInclBpm(configuration, exchangeCars, accessories, controlCodes) {
    let configPriceExclBTWinclBPM = 0.0

    if (configuration !== null && configuration.price !== null) {
      configPriceExclBTWinclBPM = parseFloat(configuration.price.ExclBTWinclBPM)
    }

    let exchangeCarsPrice = 0

    if (Array.isArray(exchangeCars)) {
      exchangeCarsPrice = exchangeCars
        .filter((car) => !!car?.Price?.Excl)
        .map((car) => parseFloat(car.Price.Excl))
        .reduce((sum, price) => sum + price, 0)

      exchangeCarsPrice += exchangeCars
        .filter((car) => !!car?.ResidualBpm)
        .map((car) => parseFloat(car.ResidualBpm))
        .reduce((sum, price) => sum + price, 0)
    }

    let dealerExtrasTotalExclBTWinclBPM = accessories
      .map((x) => parseFloat(x.Price.Excl || 0) + parseFloat(x.Price.Bpm || 0))
      .reduce(arrayFunctions.sum, 0)
    let controlCodesTotalExclBTWinclBPM = controlCodes
      .map((x) => parseFloat(x.Price.Excl || 0) + parseFloat(x.Price.Bpm || 0))
      .reduce(arrayFunctions.sum, 0)

    return (
      configPriceExclBTWinclBPM +
      dealerExtrasTotalExclBTWinclBPM +
      controlCodesTotalExclBTWinclBPM -
      exchangeCarsPrice
    )
  },
}

export default {
  state: () => ({
    quote: null,
    prevQuote: null,
    car: null,
    loading: false,
    isSaving: false,
    isCreating: false,
    shouldUpdateEmployee: false,
    shouldUpdateEstablishment: false,
    shouldCreateMatchingStockQuote: false,
    createMatchingStockQuoteOptions: null,
    updateTimeout: null,
    updateLatestFunction: null,
    previewSaveTimeout: null,
    previewSaveLatestFunction: null,
    tempLatestVwpfsCalls: {},
  }),
  getters,
  mutations,
  actions,
  namespaced: true,
  functions,
  modules: {
    printOptions,
  },
}
